import { useCallback } from 'react'

import { useEditor } from '@craftjs/core'
import domtoimage from 'dom-to-image-more'

export function useSnapshotBlock(nodeId) {
  const { node } = useEditor(state => ({
    node: state.nodes[nodeId]?.dom,
  }))

  return useCallback(async () => {
    if (!node) {
      return null
    }

    try {
      return await domtoimage.toBlob(node, {
        scale: 0.9,
        ignoreCSSRuleErrors: true,
        onclone: clone => handleFonts(node, clone),
        requestInterceptor: handleProxy,
      })
    } catch (error) {
      console.error('[snapshot-block] Failed to snapshot block to blob', {
        nodeId,
        node,
        tagName: node?.tagName,
        error,
        message: error?.message,
        stack: error?.stack,
      })

      throw error
    }
  }, [node, nodeId])
}

async function handleFonts(node, clone) {
  const fonts = await getFonts(node.ownerDocument)

  if (!fonts) {
    return;
  }

  const style = clone.ownerDocument.createElement('style')
  style.textContent = fonts
  clone.insertBefore(style, clone.firstChild)
}

async function handleProxy(url, { status }) {
  if (status !== undefined) {
    return undefined
  }

  const newURL = getURL(url)

  if (!newURL) {
    return undefined
  }

  const image = await fetchData(newURL)

  return image || undefined
}

async function getFonts(node) {
  const fontMapping = {}
  const fontRegex = /fonts\.googleapis\.com|rsms\.me/

  for (const styleSheet of node.styleSheets) {
    const { href } = styleSheet
    if (href && fontRegex.test(href)) {
      fontMapping[href] = true
    }

    try {
      const cssRules = styleSheet.cssRules || []
      for (const cssRule of cssRules) {
        const { type, href } = cssRule
        if (type === CSSRule.IMPORT_RULE && fontRegex.test(href || '')) {
          fontMapping[cssRule.href] = true
        }
      }
    } catch {
      // Cross-origin stylesheet
    }
  }

  const fonts = Object.keys(fontMapping)

  if (!fonts.length) {
    return null
  }

  const styleSheets = await Promise.all(fonts.map(async href => {
    const response = await fetch(href).catch(() => null)
    if (!response?.ok) {
      return null
    }
    return response.text()
  }))

  let cssFonts = styleSheets.join('\n')

  const urls = [...cssFonts.matchAll(/url\((https?:\/\/[^)'"]+)\)/g)].map(match => match[1])

  await Promise.all(urls.map(async url => {
    const dataURL = await fetchData(url).catch(() => null)
    if (dataURL) {
      cssFonts = cssFonts.replaceAll(url, dataURL)
    }
  }))

  return cssFonts
}

function getURL(url) {
  const match = url.match(/https?:\/\/((?:dpe|ppe)-userenroll-assets)\.s3\.amazonaws\.com\/(?:.*\/)?([^/?#]+)/i)
  if (!match) {
    return null
  }

  const [, bucket, key] = match
  const params = JSON.stringify({ bucket, key, edits: {} })

  return `https://d3s8uvz3bmynpw.cloudfront.net/${btoa(params)}`
}

async function fetchData(url) {
  const response = await fetch(url)

  if (!response.ok) {
    return null
  }

  const blob = await response.blob()

  return await new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(reader.result)
    reader.onerror = reject
    reader.readAsDataURL(blob)
  })
}
